import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Home, Calendar } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";

/**
 * AdminLayout — guards all /admin/* routes (except /admin/login).
 * On mount, calls /auth/me. If 401, redirects to /admin/login.
 */
export default function AdminLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const [authed, setAuthed] = useState(null); // null = checking, true/false = decided

  useEffect(() => {
    adminApi
      .me()
      .then(() => setAuthed(true))
      .catch(() => {
        setAuthed(false);
        nav("/admin/login", { replace: true, state: { from: loc.pathname } });
      });
  }, []);

  const handleLogout = async () => {
    try {
      await adminApi.logout();
      toast.success("Logged out");
      nav("/admin/login", { replace: true });
    } catch (e) {
      toast.error("Logout failed");
    }
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink text-parchment">
        <div className="font-serif text-sm tracking-widest opacity-60">CHECKING…</div>
      </div>
    );
  }
  if (!authed) return null;

  return (
    <div className="min-h-screen bg-ink text-parchment" data-testid="admin-layout">
      <header className="border-b border-parchment-faint">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="font-serif text-2xl tracking-tight" data-testid="admin-home-link">
            Lev<span className="text-four-yellow">.</span> Admin
          </Link>
          <nav className="flex items-center gap-6 text-xs tracking-widest">
            <Link
              to="/admin"
              className={`flex items-center gap-2 hover:text-four-yellow transition-colors ${
                loc.pathname === "/admin" ? "text-four-yellow" : ""
              }`}
              data-testid="admin-nav-today"
            >
              <Home size={14} /> TODAY
            </Link>
            <Link
              to="/admin/planning"
              className={`flex items-center gap-2 hover:text-four-yellow transition-colors ${
                loc.pathname.startsWith("/admin/planning") ? "text-four-yellow" : ""
              }`}
              data-testid="admin-nav-planning"
            >
              <Calendar size={14} /> PLANNING
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 hover:text-four-yellow transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> LOGOUT
            </button>
          </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
